import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { legalPath } from "@/data/legalPaths";

const COOKIE_CONSENT_KEY = "intygslakaren_cookie_consent";
const COOKIE_PREFERENCES_KEY = "intygslakaren_cookie_preferences";
const LEGACY_CONSENT_STORAGE_KEY = "cookie-consent";
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

/** Re-open the cookie consent UI (e.g. from the footer). */
export const openCookieSettings = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};

type CookiePreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

type ConsentState = {
  hasConsented: boolean;
  preferences: CookiePreferences;
  timestamp: number | null;
};

export const CookieBanner = () => {
  const { t, lang } = useLang();
  const cookiePolicyHref = legalPath("cookies", lang);
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const hasAccepted = Boolean(window.localStorage.getItem(COOKIE_CONSENT_KEY));

    const savedPreferences = window.localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences) as Partial<CookiePreferences>;
        setPreferences({
          necessary: true,
          analytics: Boolean(parsed.analytics),
          marketing: Boolean(parsed.marketing),
        });
      } catch {
        // Ignore malformed stored data and keep defaults.
      }
    }

    setVisible(!hasAccepted);
  }, []);

  useEffect(() => {
    if (visible) {
      document.body.setAttribute("data-cookie-banner-open", "");
    } else {
      document.body.removeAttribute("data-cookie-banner-open");
    }
    return () => document.body.removeAttribute("data-cookie-banner-open");
  }, [visible]);

  useEffect(() => {
    const handleOpen = () => {
      const savedPreferences = window.localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences) as Partial<CookiePreferences>;
          setPreferences({
            necessary: true,
            analytics: Boolean(parsed.analytics),
            marketing: Boolean(parsed.marketing),
          });
        } catch {
          // Keep current preferences if stored data is malformed.
        }
      }
      setVisible(true);
      setSettingsOpen(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
  }, []);

  const persistConsent = (prefs: CookiePreferences, consentType: "accepted" | "custom" | "declined") => {
    const state: ConsentState = {
      hasConsented: true,
      preferences: prefs,
      timestamp: Date.now(),
    };

    window.localStorage.setItem(COOKIE_CONSENT_KEY, consentType);
    window.localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    // Keep compatibility with old site's storage format.
    window.localStorage.setItem(LEGACY_CONSENT_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent("consent-updated", { detail: state }));
  };

  const acceptCookies = () => {
    persistConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
      },
      "accepted",
    );
    setVisible(false);
    setSettingsOpen(false);
  };

  const declineCookies = () => {
    persistConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
      },
      "declined",
    );
    setVisible(false);
    setSettingsOpen(false);
  };

  const saveSettings = () => {
    persistConsent(preferences, "custom");
    setVisible(false);
    setSettingsOpen(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-4">
      <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card p-4 shadow-elevated sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-foreground sm:pr-4">
            {t.cookies.text}{" "}
            <Link to={cookiePolicyHref} className="font-medium text-primary underline-offset-2 hover:underline">
              {t.cookies.policyLink}
            </Link>
            .
          </p>
          <div className="flex flex-row gap-2 sm:shrink-0">
            <Button type="button" variant="outline" onClick={() => setSettingsOpen((open) => !open)}>
              {t.cookies.settings}
            </Button>
            <Button type="button" variant="outline" onClick={declineCookies}>
              {t.cookies.decline}
            </Button>
            <Button type="button" variant="hero" onClick={acceptCookies}>
              {t.cookies.accept}
            </Button>
          </div>
        </div>
        {settingsOpen && (
          <div className="mt-4 space-y-4 rounded-2xl border border-border bg-background p-4 sm:p-5">
            <div>
              <h2 className="font-display text-xl text-foreground sm:text-2xl">{t.cookies.modalTitle}</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t.cookies.modalDescription}</p>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{t.cookies.necessaryTitle}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t.cookies.necessaryText}</p>
                  </div>
                  <input type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded border-input" />
                </div>
              </div>
              <div className="rounded-2xl border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{t.cookies.analyticsTitle}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t.cookies.analyticsText}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(event) => setPreferences((prev) => ({ ...prev, analytics: event.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-input"
                  />
                </div>
              </div>
              <div className="rounded-2xl border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{t.cookies.marketingTitle}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t.cookies.marketingText}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(event) => setPreferences((prev) => ({ ...prev, marketing: event.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={acceptCookies}>
                {t.cookies.acceptAll}
              </Button>
              <Button type="button" variant="hero" onClick={saveSettings}>
                {t.cookies.saveSettings}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
